import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { Percent } from '@mui/icons-material';

interface UserOperatorCommission {
  operatorId: number;
  operatorCode: string;
  operatorName: string;
  commissionRate: number; // Admin-set commission rate
  currentSalesPercentage: number; // User's current sales percentage
}

interface UserCommissionSummary {
  userId: number;
  username: string;
  fullName: string;
  operatorCommissions: UserOperatorCommission[];
}

const CommissionDisplay: React.FC = () => {
  const { data: commissions, isLoading, error } = useQuery<UserCommissionSummary>({
    queryKey: ['user-commissions'],
    queryFn: async () => {
      // Fetch both commission rates and sales percentages
      const [commissionResponse, salesResponse] = await Promise.all([
        axios.get('/user/my-commissions'),
        axios.get('/user/commission-management')
      ]);

      const commissionData = commissionResponse.data;
      const salesData = salesResponse.data;

      // Merge the data
      return {
        userId: salesData.userId,
        username: salesData.username,
        fullName: salesData.fullName,
        operatorCommissions: commissionData.operatorCommissions.map((commission: any) => {
          const salesOperator = salesData.operatorCommissions.find((so: any) => so.operatorId === commission.operatorId);
          return {
            operatorId: commission.operatorId,
            operatorCode: commission.operatorCode,
            operatorName: commission.operatorName,
            commissionRate: commission.commissionPercentage, // From admin
            currentSalesPercentage: salesOperator?.currentSalesPercentage || 0
          };
        })
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // 10 minutes
  });

  if (isLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={20} />
          <Typography>Loading sales percentages...</Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load sales percentages
        </Alert>
      </Paper>
    );
  }

  if (!commissions || commissions.operatorCommissions.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Percent color="primary" />
          <Typography variant="h6">Sales Percentages</Typography>
        </Box>
        <Typography color="text.secondary">
          No sales percentages configured
        </Typography>
      </Paper>
    );
  }

  const averageSalesRate = commissions.operatorCommissions.reduce(
    (sum, commission) => sum + commission.currentSalesPercentage, 
    0
  ) / commissions.operatorCommissions.length;

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Percent color="primary" />
        <Typography variant="h6">Your Sales Percentages</Typography>
        <Chip 
          label={`Avg: ${(averageSalesRate * 100).toFixed(2)}%`} 
          color="primary" 
          size="small"
          variant="outlined"
        />
      </Box>

      <Grid container spacing={2}>
        {commissions.operatorCommissions.map((commission) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={commission.operatorId}>
            <Box 
              sx={{ 
                p: 2, 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1,
                bgcolor: 'background.paper',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main'
                }
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  {commission.operatorCode}
                </Typography>
                <Box display="flex" gap={1}>
                  <Chip
                    label={`Sales: ${(commission.currentSalesPercentage * 100).toFixed(2)}%`}
                    color="success"
                    size="small"
                  />
                  <Chip
                    label={`Rate: ${(commission.commissionRate * 100).toFixed(2)}%`}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
              <Typography variant="body1" fontWeight={500}>
                {commission.operatorName}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box mt={2}>
        <Typography variant="caption" color="text.secondary">
          Commission rates are set by admin and cannot be changed. Sales percentages are your share of total sales and can be managed in Sales Management.
        </Typography>
      </Box>
    </Paper>
  );
};

export default CommissionDisplay;
